import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  FileText,
  ChevronRight,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  PlayCircle,
} from "lucide-react";
import { releasesApi, servicesApi, Release } from "../api/client";
import { formatRelativeTime, titleCase } from "../lib/utils";

const statusConfig: Record<
  Release["status"],
  { label: string; icon: React.ReactNode; className: string }
> = {
  draft: {
    label: "Draft",
    icon: <FileText className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-700",
  },
  ready: {
    label: "Ready",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-blue-100 text-blue-700",
  },
  in_progress: {
    label: "In Progress",
    icon: <PlayCircle className="w-4 h-4" />,
    className: "bg-yellow-100 text-yellow-700",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  cancelled: {
    label: "Cancelled",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
};

function ReleaseStatusBadge({ status }: { status: Release["status"] }) {
  const config = statusConfig[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

export default function Releases() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");

  const serviceFilter = searchParams.get("service") || "";
  const statusFilter = searchParams.get("status") || "";

  const { data: releasesData, isLoading: releasesLoading } = useQuery({
    queryKey: ["releases", serviceFilter, statusFilter],
    queryFn: () =>
      releasesApi.list({
        service: serviceFilter || undefined,
        status: statusFilter || undefined,
      }),
  });

  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const releases = releasesData?.releases || [];
  const services = servicesData?.services || [];

  const filteredReleases = releases.filter((release) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      release.title.toLowerCase().includes(query) ||
      release.version.toLowerCase().includes(query) ||
      release.service_name?.toLowerCase().includes(query)
    );
  });

  const handleServiceChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("service", value);
    } else {
      params.delete("service");
    }
    setSearchParams(params);
  };

  const handleStatusChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("status", value);
    } else {
      params.delete("status");
    }
    setSearchParams(params);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Releases</h1>
          <p className="text-gray-600 mt-1">
            Manage deployment release notes and track execution across
            environments
          </p>
        </div>
        <button
          onClick={() => navigate("/releases/new")}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Release
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search releases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          {/* Service Filter */}
          <div className="w-48">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={serviceFilter}
                onChange={(e) => handleServiceChange(e.target.value)}
                className="input pl-10 w-full appearance-none"
              >
                <option value="">All Services</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {titleCase(service.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-40">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="input w-full"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="ready">Ready</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Release List */}
      {releasesLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : filteredReleases.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No releases found
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || serviceFilter || statusFilter
              ? "Try adjusting your filters"
              : "Get started by creating your first release"}
          </p>
          {!searchQuery && !serviceFilter && !statusFilter && (
            <button
              onClick={() => navigate("/releases/new")}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Release
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Release
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReleases.map((release) => (
                <tr
                  key={release.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/releases/${release.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {release.title}
                        </div>
                        {release.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {release.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {release.service_name
                        ? titleCase(release.service_name)
                        : "-"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
                      {release.version}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ReleaseStatusBadge status={release.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{formatRelativeTime(release.created_at)}</div>
                    {release.created_by_name && (
                      <div className="text-xs text-gray-400">
                        by {release.created_by_name}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/releases/${release.id}`);
                      }}
                      className="text-brand-600 hover:text-brand-700"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
